import React from 'react';
import { Input } from '../../Input';
import { Tooltip } from '../../Tooltip';
import { Question } from '../../../../types/api';
import {
  RadioGroup,
  RadioOption,
  RadioInput,
} from '../DynamicQuestionForm.styled';
import {
  createAnswerValue,
  shouldShowTextInput,
  getInputType,
  getPlaceholder,
  getInputProps,
  getFieldStyles,
} from '../utils/questionFieldUtils';
import { useHotelQuestionnaireAnswers } from '../../../../hooks';

interface RadioFieldRendererProps {
  question: Question;
  value: any;
  renderUploadSection: () => React.ReactNode;
  shouldShowUploadSection: () => boolean;
  renderApproveSection: () => React.ReactNode;
  currentStepKey?: string;
}

/**
 * Render radio options component
 */
export const renderRadioOptions = (
  question: Question,
  selectedValue: string | null,
  updateDraftAnswer: (questionId: string, value: any) => void,
  textValue: string,
  questionType: string,
  currentStepKey?: string
) => (
  <RadioGroup>
    {question.options?.map((option) => (
      <RadioOption key={String(option.id)}>
        <RadioInput
          type="radio"
          name={question.id}
          value={String(option.id)}
          checked={String(selectedValue ?? question.defaultValue) === String(option.id)}
          disabled={question?.attributes?.readOnly || false}
          onChange={(e) => {
            if (currentStepKey === 'sources') {
              return;
            }

            const newTextValue = questionType === 'radio/text' ? '' : textValue;
            //for radio+text type, if the selected option is not in show_on_options, clear the text input value as text input is dependent on selected option
            if(questionType === 'radio-text' && !question.validation_rules?.show_on_options?.includes(e.target.value)) {
              updateDraftAnswer(question.id, createAnswerValue(option.id, ""));
            }
            else {
              updateDraftAnswer(question.id, createAnswerValue(e.target.value, newTextValue));
            }
          }}
          required={question.required}
          style={getFieldStyles(question)}
          {...question.attributes}
        />
        <div style={getFieldStyles(question, { position: 'relative' })}>
          <span style={{ display: 'flex', alignItems: 'center' }}>
            {option.label}
            {option.infotext && <Tooltip content={option.infotext} position="top" maxWidth={240}/>}
          </span>
        </div>
      </RadioOption>
    ))}
  </RadioGroup>
);

/**
 * Render text input component
 */
export const renderTextInput = (
  question: Question,
  textValue: string,
  radioValue: string | null,
  updateDraftAnswer: (questionId: string, value: any) => void,
  questionType: string,
  currentStepKey?: string
) => (
  <div style={{ margin: 'var(--spacing-3) 0' }}>
    <Input
      type={getInputType(question)}
      value={textValue}
      onChange={(e) => {
        if (currentStepKey === 'sources') {
          return;
        }
        const newRadioValue = questionType === 'radio/text' ? null : radioValue;
        updateDraftAnswer(question.id, createAnswerValue(newRadioValue, e.target.value));
      }}
      showValidationError={['url', 'email', 'phone'].includes(question.validation_rules?.data_type || '')}
      {...question.attributes}
      placeholder={getPlaceholder(question)}
      {...getInputProps(question)}
      style={{
        cursor: question.attributes?.readOnly ? 'not-allowed' : 'text',
        ...question.attributes?.style,
      }}
    />
  </div>
);

/**
 * Main component for rendering radio-text fields
 */
export const RadioFieldRenderer: React.FC<RadioFieldRendererProps> = ({
  question,
  value,
  renderUploadSection,
  renderApproveSection,
  shouldShowUploadSection,
  currentStepKey,
}) => {
  const { updateDraftAnswer } = useHotelQuestionnaireAnswers();
  const radioValue = value?.selectedOptionId;
  const textValue = value?.typedValue || '';
  const questionType = question.type;

  return (
    <div>
      {renderRadioOptions(question, radioValue, updateDraftAnswer, textValue, questionType, currentStepKey)}
      
      {shouldShowTextInput(questionType, radioValue, question) && 
        renderTextInput(question, textValue, radioValue, updateDraftAnswer, questionType, currentStepKey)
      }
      
      {shouldShowUploadSection() && renderUploadSection()}
      {renderApproveSection()}
    </div>
  );
};